import type { MyAcknowledgementRequestDto } from "@/generated/model";
import { MyAcknowledgementRequestDtoState } from "@/generated/model";

/**
 * Is this request still work for the staff member?
 *
 * VOIDED is neither outstanding nor history: it is a document that stopped being
 * current before they got to it, so it belongs on no list (AC22).
 */
export function isOutstanding(request: MyAcknowledgementRequestDto): boolean {
  return request.state === MyAcknowledgementRequestDtoState.OUTSTANDING;
}

export function isOverdue(
  request: MyAcknowledgementRequestDto,
  now: Date,
): boolean {
  if (!request.dueAt) {
    return false;
  }
  return new Date(request.dueAt).getTime() < now.getTime();
}

/**
 * Everything outstanding belongs in Today, always.
 *
 * AC10 says the request "remains there until actioned". Unlike a checklist or a
 * compliance task, an acknowledgement has no availability window — there is no
 * "not due yet, so show it under Upcoming". So the Upcoming bucket is
 * permanently empty, the same shape the follow-up actions bucket already has.
 */
export function partitionAcknowledgements(
  requests: MyAcknowledgementRequestDto[],
  now: Date,
): {
  today: MyAcknowledgementRequestDto[];
  upcoming: MyAcknowledgementRequestDto[];
} {
  return {
    today: sortAcknowledgements(requests.filter(isOutstanding), now),
    upcoming: [],
  };
}

/**
 * Overdue first, then dated before undated, then soonest deadline, then a stable
 * id tiebreak.
 *
 * This is the rule the To do list's follow-up actions already use, chosen for
 * consistency rather than novelty — two row types in one list sorting by
 * different rules reads as a bug.
 */
export function sortAcknowledgements(
  requests: MyAcknowledgementRequestDto[],
  now: Date,
): MyAcknowledgementRequestDto[] {
  return [...requests].sort((a, b) => {
    const aOverdue = isOverdue(a, now);
    const bOverdue = isOverdue(b, now);
    if (aOverdue !== bOverdue) {
      return aOverdue ? -1 : 1;
    }
    if (!a.dueAt !== !b.dueAt) {
      // Undated last: an item with no deadline is not more urgent than one with.
      return a.dueAt ? -1 : 1;
    }
    if (a.dueAt && b.dueAt && a.dueAt !== b.dueAt) {
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    }
    return a.requestId.localeCompare(b.requestId);
  });
}

/** Human due text for a row. Null when there is no deadline. */
export function dueLabel(
  request: Pick<MyAcknowledgementRequestDto, "dueAt">,
  now: Date,
): { text: string; overdue: boolean } {
  if (!request.dueAt) {
    return { text: "No due date", overdue: false };
  }
  const due = new Date(request.dueAt);
  const overdue = due.getTime() < now.getTime();
  const days = Math.abs(
    Math.round((due.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
  );
  if (overdue) {
    return { text: days === 0 ? "Overdue" : `${days} day(s) overdue`, overdue };
  }
  return {
    text: days === 0 ? "Due today" : `Due in ${days} day(s)`,
    overdue: false,
  };
}
